import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bed as BedIcon, Plus, ArrowRight, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format, differenceInCalendarDays } from 'date-fns';
import { getAdmissions, type AdmissionRow } from '@/lib/ipdApi';
import AdmitPatientModal from '@/components/IPD/AdmitPatientModal';

const STATUS_STYLES: Record<string, string> = {
  admitted:     'bg-primary/10 text-primary',
  discharged:   'bg-success/10 text-success',
  lama:         'bg-warning/10 text-warning',
  absconded:    'bg-destructive/10 text-destructive',
  referred_out: 'bg-muted text-muted-foreground',
};
const STATUS_LABELS: Record<string, string> = {
  referred_out: 'Referred Out',
};

function stayLength(a: AdmissionRow): string {
  const start = new Date(a.admission_date);
  const end = a.discharge_date ? new Date(a.discharge_date) : new Date();
  const days = Math.max(1, differenceInCalendarDays(end, start) + 1);
  return `${days} day${days !== 1 ? 's' : ''}`;
}

interface PatientIPDTabProps {
  patientId: string;
  patient: { name: string; age?: number; gender?: string; phone?: string; mrn?: string };
  clinicId: string;
  canWrite: boolean;
}

export default function PatientIPDTab({ patientId, patient, clinicId, canWrite }: PatientIPDTabProps) {
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState<AdmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [admitOpen, setAdmitOpen] = useState(false);

  const load = useCallback(() => {
    if (!clinicId) return;
    setLoading(true);
    getAdmissions(clinicId, { status: 'all', patientId })
      .then(data => setAdmissions(data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clinicId, patientId]);

  useEffect(() => { load(); }, [load]);

  const activeAdmission = admissions.find(a => a.status === 'admitted');

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <BedIcon className="w-4 h-4 text-muted-foreground" />
          IPD History
          {!loading && admissions.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              ({admissions.length} admission{admissions.length !== 1 ? 's' : ''})
            </span>
          )}
        </h3>
        {canWrite && !activeAdmission && (
          <Button size="sm" onClick={() => setAdmitOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Admit to IPD
          </Button>
        )}
      </div>

      {/* Active admission banner */}
      {activeAdmission && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 flex items-center justify-between">
          <div className="text-sm">
            <span className="font-medium text-foreground">Currently Admitted</span>
            <span className="text-muted-foreground">
              {' '}— {activeAdmission.ward_name}, Bed {activeAdmission.bed_number} · Adm# {activeAdmission.admission_number}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate(`/ipd/admissions/${activeAdmission.id}`, { state: { from: `/patients/${patientId}` } })}>
            View <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      )}

      {/* Admission list */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : admissions.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">No IPD admissions found.</div>
      ) : (
        <div className="space-y-2">
          {admissions.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => navigate(`/ipd/admissions/${a.id}`, { state: { from: `/patients/${patientId}` } })}
              className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{a.admission_number}</span>
                  <span className="text-sm font-medium text-foreground">{a.ward_name}, Bed {a.bed_number}</span>
                  <span className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                    STATUS_STYLES[a.status] ?? 'bg-muted text-muted-foreground',
                  )}>
                    {STATUS_LABELS[a.status] ?? a.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {a.admission_date ? format(new Date(a.admission_date), 'dd MMM yyyy') : '—'}
                  {' – '}
                  {a.discharge_date ? format(new Date(a.discharge_date), 'dd MMM yyyy') : 'Present'}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                <span>Doctor: {a.admitting_doctor_name || '—'}</span>
                <span className="flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" /> {stayLength(a)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Admit Patient Modal */}
      {clinicId && (
        <AdmitPatientModal
          open={admitOpen}
          onClose={() => setAdmitOpen(false)}
          clinicId={clinicId}
          initialPatient={{
            id: patientId,
            name: patient.name,
            age: patient.age,
            gender: patient.gender,
            phone: patient.phone,
            mrn: patient.mrn,
          }}
          onAdmitted={id => {
            setAdmitOpen(false);
            navigate(`/ipd/admissions/${id}`, { state: { from: `/patients/${patientId}` } });
          }}
        />
      )}
    </div>
  );
}
